import { Box, Switch } from '@mui/material';
import {
  type Control,
  type FieldPathByValue,
  type FieldValues,
  type PathValue,
  type UseFormSetValue,
  Controller,
} from 'react-hook-form';
import { EyeIconButton } from 'src/features/components/editComponentPage/forms/cod/messagingTitle/eyeIconButton/EyeIconButton';

type HasAdsConfig = FieldValues & {
  ads?: {
    isAdsEnabled?: boolean;
    showPlaceholder?: boolean;
  };
};

interface AllowAdsToggleProps<T extends HasAdsConfig = HasAdsConfig> {
  control: Control<T>;
  handleOnChange: (key: string, value: unknown) => void;
  setValue: UseFormSetValue<T>;
  showEyeIconButton?: boolean;
  showPlaceholderRef?: React.MutableRefObject<boolean>;
}

const ADS_IS_ENABLED_PATH = 'ads.isAdsEnabled';
const ADS_SHOW_PLACEHOLDER_PATH = 'ads.showPlaceholder';

export const AllowAdsToggle = <T extends HasAdsConfig = HasAdsConfig>({
  control,
  handleOnChange,
  setValue,
  showEyeIconButton = true,
  showPlaceholderRef,
}: AllowAdsToggleProps<T>) => {
  const adsIsEnabledPath = ADS_IS_ENABLED_PATH as FieldPathByValue<
    T,
    boolean | undefined
  >;
  const adsShowPlaceholderPath = ADS_SHOW_PLACEHOLDER_PATH as FieldPathByValue<
    T,
    boolean | undefined
  >;
  const defaultFalse = false as PathValue<
    T,
    FieldPathByValue<T, boolean | undefined>
  >;

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={0.5}
      mr={-0.5}
      onClick={(event) => event.stopPropagation()}
      onFocus={(event) => event.stopPropagation()}>
      <Controller
        name={adsIsEnabledPath}
        control={control}
        defaultValue={defaultFalse}
        render={({ field: { ref, value, onChange, ...field } }) => (
          <>
            {Boolean(value) && showEyeIconButton && (
              <Controller
                name={adsShowPlaceholderPath}
                control={control}
                defaultValue={defaultFalse}
                render={({ field: showPlaceholderField }) => (
                  <EyeIconButton
                    isHighlighted={Boolean(showPlaceholderField.value)}
                    showOffWhenDisabled
                    keepInheritedColorWhenHighlighted
                    onClick={(event) => {
                      event.stopPropagation();
                      const nextValue = !showPlaceholderField.value;

                      handleOnChange('ads.showPlaceholder', nextValue);
                      showPlaceholderField.onChange(nextValue);
                      if (showPlaceholderRef) {
                        showPlaceholderRef.current = nextValue;
                      }
                    }}
                  />
                )}
              />
            )}
            <Switch
              {...field}
              checked={Boolean(value)}
              inputRef={ref}
              sx={{
                mr: -0.5,
              }}
              onChange={(event) => {
                const nextValue = event.target.checked;

                handleOnChange('ads.isAdsEnabled', nextValue);
                onChange(nextValue);
                setValue(
                  adsIsEnabledPath,
                  nextValue as PathValue<T, typeof adsIsEnabledPath>,
                  {
                    shouldDirty: true,
                  },
                );
              }}
            />
          </>
        )}
      />
    </Box>
  );
};
